/**
 * The Activity tab's health strip — six tiles answering "is CDID healthy",
 * "what is it spending" and "is anything stuck" at a glance, above the cycle
 * table.
 *
 * Purely presentational: `health.verdict` and `health.stuck` are already
 * derived server-side (from the lander's own wave reports, not from age —
 * see `CdidHealth`'s doc comment on `packages/types/src/autobuild.ts`), so
 * this file only renders what it is given and does no health judgment of its
 * own — the same split `SlotsSections.tsx` and `slots-display.ts` use.
 */
import type { CdidHealth, CdidHealthVerdict } from '@a7y/types';
import { fmtHm, relativeAgo } from './shared';

const VERDICT_META: Record<CdidHealthVerdict, { label: string; className: string }> = {
  healthy: { label: 'Healthy', className: 'text-emerald-600 dark:text-emerald-400' },
  degraded: { label: 'Degraded', className: 'text-amber-600 dark:text-amber-400' },
  stuck: { label: 'Stuck', className: 'text-red-600 dark:text-red-400' },
};

/**
 * Fraction of the current UTC calendar day elapsed, `[0, 1)`. `byDay` is a
 * UTC-day series (see `CdidSpendDay.day`), so "is today more than half over"
 * must be judged in UTC too — a local-time fraction would flag a spike an
 * hour before the UTC day it is measured against is even half done.
 */
function utcDayFractionElapsed(nowMs: number): number {
  const d = new Date(nowMs);
  const secondsSinceMidnight =
    d.getUTCHours() * 3600 + d.getUTCMinutes() * 60 + d.getUTCSeconds();
  return secondsSinceMidnight / 86_400;
}

function Tile({
  label,
  tone = 'normal',
  children,
}: {
  label: string;
  tone?: 'normal' | 'warn' | 'bad';
  children: React.ReactNode;
}) {
  const ring =
    tone === 'bad'
      ? 'border-red-200 dark:border-red-800'
      : tone === 'warn'
        ? 'border-amber-200 dark:border-amber-800'
        : 'border-zinc-200 dark:border-zinc-800';
  return (
    <div className={`min-w-0 flex-1 rounded-lg border bg-white p-3 dark:bg-zinc-900 ${ring}`}>
      <div className="text-[10px] font-medium uppercase tracking-wide text-zinc-400">{label}</div>
      <div className="mt-1 space-y-0.5">{children}</div>
    </div>
  );
}

/** Eight bars, plain divs — this is a glance-scale spike indicator, not a
 *  chart, and pulling in a charting library for eight rectangles would be the
 *  opposite of that. */
function SpendBars({ byDay }: { byDay: CdidHealth['spend']['byDay'] }) {
  if (byDay.length === 0) return null;
  const max = Math.max(1, ...byDay.map((d) => d.workerSeconds));
  return (
    <div className="mt-1.5 flex h-5 items-end gap-0.5" aria-hidden>
      {byDay.map((d) => (
        <div
          key={d.day}
          title={`${d.day}: ${fmtHm(d.workerSeconds)} worker time, ${d.attempts} attempt${d.attempts === 1 ? '' : 's'}, ${d.landed} landed`}
          className="flex-1 rounded-sm bg-blue-300 dark:bg-blue-700"
          style={{ height: `${Math.max(8, (d.workerSeconds / max) * 100)}%` }}
        />
      ))}
    </div>
  );
}

export function CdidHealthStrip({ health, now }: { health: CdidHealth; now: number }) {
  const verdict = VERDICT_META[health.verdict];
  const spikeThreshold = health.spend.workerSeconds7dAvgPerDay * 1.5;
  const dayHalfOver = utcDayFractionElapsed(now) > 0.5;
  const spendSpiking = dayHalfOver && health.spend.workerSecondsToday > spikeThreshold;
  const landerLate =
    health.landerLastRanAt != null && now - Date.parse(health.landerLastRanAt) > 90 * 60_000;

  return (
    <div className="flex flex-wrap gap-3">
      <Tile label="Status" tone={health.verdict === 'healthy' ? 'normal' : health.verdict === 'stuck' ? 'bad' : 'warn'}>
        <div className={`text-sm font-semibold ${verdict.className}`}>{verdict.label}</div>
        {health.reasons.length > 0 && (
          <ul className="space-y-0.5 text-[11px] text-zinc-500 dark:text-zinc-400">
            {health.reasons.map((reason, i) => (
              <li key={i}>{reason}</li>
            ))}
          </ul>
        )}
      </Tile>

      <Tile label="Last landing">
        <div className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">
          {relativeAgo(health.lastLandedAt, now)}
        </div>
        <div className="text-[11px] text-zinc-500 dark:text-zinc-400">
          {health.landed24h} in 24h · {health.landed7d} in 7d
        </div>
      </Tile>

      <Tile label="In flight">
        <div className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">
          {health.inFlightCycles} cycle{health.inFlightCycles === 1 ? '' : 's'}
        </div>
        <div className="text-[11px] text-zinc-500 dark:text-zinc-400">
          {health.inFlightWorkers} worker{health.inFlightWorkers === 1 ? '' : 's'}
        </div>
      </Tile>

      <Tile label="Stuck" tone={health.stuck.length > 0 ? 'bad' : 'normal'}>
        <div
          className={`text-sm font-semibold ${health.stuck.length > 0 ? 'text-red-600 dark:text-red-400' : 'text-zinc-800 dark:text-zinc-200'}`}
        >
          {health.stuck.length}
        </div>
        {health.stuck.length > 0 && (
          <ul className="space-y-0.5 text-[11px] text-red-700 dark:text-red-300">
            {health.stuck.map((bundle) => (
              <li key={bundle.bundleId}>
                {bundle.ticketId ?? bundle.bundleId} — blocked {bundle.wavesBlocked} wave
                {bundle.wavesBlocked === 1 ? '' : 's'}, since {relativeAgo(bundle.blockedSince, now)}
              </li>
            ))}
          </ul>
        )}
      </Tile>

      <Tile label="Spend" tone={spendSpiking ? 'warn' : 'normal'}>
        <div
          className={`text-sm font-semibold ${spendSpiking ? 'text-amber-600 dark:text-amber-400' : 'text-zinc-800 dark:text-zinc-200'}`}
          title="Worker minutes and model tokens are what CDID records — cost is not priced at the task boundary (cost_source: unavailable_no_cost_at_task_boundary on every worker row)."
        >
          {fmtHm(health.spend.workerSecondsToday)} today
        </div>
        <div className="text-[11px] text-zinc-500 dark:text-zinc-400">
          avg {fmtHm(health.spend.workerSeconds7dAvgPerDay)}
          {health.spend.attemptsPerTicket7d != null &&
            ` · ${health.spend.attemptsPerTicket7d.toFixed(1)} attempts/ticket`}
        </div>
        <SpendBars byDay={health.spend.byDay} />
      </Tile>

      <Tile label="Lander" tone={landerLate ? 'warn' : 'normal'}>
        <div
          className={`text-sm font-semibold ${landerLate ? 'text-amber-600 dark:text-amber-400' : 'text-zinc-800 dark:text-zinc-200'}`}
        >
          {health.landerLastRanAt != null ? relativeAgo(health.landerLastRanAt, now) : 'No wave in window'}
        </div>
      </Tile>
    </div>
  );
}
